// A project in the studio: its details, then the planning and the estimate
// as the same two tabs the client sees. The planning shows its timeline live
// above the phases; the estimate adds up as you type. Nothing is stored until
// Save. Changing an approved estimate asks the client to approve it again.
import * as React from "react"
import { totals, version, type Line, type Project } from "../../lib/auth"
import { fmtDate, fmtMoney, studioClient, studioSave, type FullClient } from "../api"
import { defaultReference } from "../../lib/studio"
import { Planning } from "../Project"
import { SaveImageButton } from "../SaveImage"
import { go } from "../route"
import { BackLink, useEntrance } from "./Studio"
import { Confirm, Field, RemoveButton, SaveBar, slugify, studioPath, useLeaveGuard, type SaveState } from "./parts"

const iso = (d: Date) => d.toISOString().slice(0, 10)
const addDays = (s: string, n: number) => {
    const d = new Date((s || iso(new Date())) + "T12:00:00")
    d.setDate(d.getDate() + n)
    return iso(d)
}

function blank(client: FullClient): Project {
    const start = addDays(iso(new Date()), 7)
    return {
        id: "",
        title: "",
        client: client.projects[0]?.client || client.name,
        reference: defaultReference(client.no),
        date: iso(new Date()),
        planning: { phases: [{ name: "Kick-off", start, end: addDays(start, 4) }] },
        estimate: { currency: "EUR", vat: 21, validUntil: addDays(iso(new Date()), 30), groups: [{ name: "Design", lines: [{ item: "", qty: 1, unit: "days", rate: 0 }] }] },
    }
}

export function ProjectEdit({ slug, projectId, onSaved }: { slug: string; projectId: string; onSaved: () => void }) {
    const [client, setClient] = React.useState<FullClient | "gone" | null>(null)
    React.useEffect(() => {
        studioClient(slug).then((c) => setClient(c ?? "gone"))
    }, [slug])
    if (!client) return null
    if (client === "gone") return <p className="p-quiet nto-mono">There's no client at /{slug}.</p>
    const isNew = projectId === "new"
    const project = isNew ? blank(client) : client.projects.find((p) => p.id === projectId)
    if (!project) return <p className="p-quiet nto-mono">There's no project at /{slug}/{projectId}.</p>
    return <Editor client={client} initial={project} isNew={isNew} onSaved={onSaved} />
}

type Tab = "planning" | "estimate"

function Editor({ client, initial, isNew, onSaved }: { client: FullClient; initial: Project; isNew: boolean; onSaved: () => void }) {
    const root = React.useRef<HTMLElement>(null)
    useEntrance(root)
    const [base, setBase] = React.useState(initial) // what's stored
    const [p, setP] = React.useState(initial)
    const [idTouched, setIdTouched] = React.useState(!isNew)
    const [tab, setTab] = React.useState<Tab>("planning")
    const [state, setState] = React.useState<SaveState>("idle")
    const id = idTouched ? p.id : slugify(p.title)
    const dirty = isNew || JSON.stringify({ ...p, id }) !== JSON.stringify(base)
    useLeaveGuard(dirty && state !== "saving")
    const approval = client.approvals[initial.id]
    const approvalAtRisk = !!approval && version(p) !== version(base)

    const edit = (f: (q: Project) => Project) => {
        setP((q) => f(structuredClone(q)))
        if (state !== "saving") setState("idle")
    }

    const save = async () => {
        if (!p.title.trim()) return setState({ error: "The project needs a title" })
        const next = { ...p, id }
        const others = client.projects.filter((q) => q.id !== initial.id)
        if (others.some((q) => q.id === next.id)) return setState({ error: `Another project already has the address /${next.id}` })
        const projects = isNew ? [...client.projects, next] : client.projects.map((q) => (q.id === initial.id ? next : q))
        setState("saving")
        const r = await studioSave({ slug: client.slug, name: client.name, logo: client.logo, projects })
        if ("error" in r) return setState({ error: r.error })
        client.projects = projects
        if (r.cleared?.length) for (const c of r.cleared) delete client.approvals[c]
        setBase(next)
        setState("saved")
        onSaved()
        if (isNew || next.id !== initial.id) go(studioPath(client.slug, next.id), { replace: true, quiet: true })
    }

    const remove = async () => {
        const projects = client.projects.filter((q) => q.id !== initial.id)
        const r = await studioSave({ slug: client.slug, name: client.name, logo: client.logo, projects })
        if ("error" in r) return setState({ error: r.error })
        onSaved()
        go(studioPath(client.slug))
    }

    const t = totals(p.estimate)
    // the timeline preview needs phases with dates
    const previewable = p.planning.phases.length > 0 && p.planning.phases.every((ph) => ph.start && ph.end && ph.end >= ph.start)

    return (
        <main ref={root} className="p-page">
            <section className="p-hero">
                <BackLink to={studioPath(client.slug)} label={client.name} />
                <div className="p-meta nto-mono" data-r>
                    <span>{isNew ? "New project" : `/${client.slug}/${base.id}`}</span>
                    {!isNew && (
                        <a className="e-link" href={`/${client.slug}/${base.id}`} target="_blank" rel="noopener">
                            View as the client sees it ↗
                        </a>
                    )}
                </div>
                <input
                    className="p-title e-title-in"
                    placeholder="Project title"
                    value={p.title}
                    autoFocus={isNew}
                    onChange={(e) => edit((q) => ((q.title = e.target.value), q))}
                    aria-label="Project title"
                    data-r
                />
                <div className="e-grid e-details" data-r>
                    <Field label="Client company">
                        <input className="e-in" value={p.client} onChange={(e) => edit((q) => ((q.client = e.target.value), q))} />
                    </Field>
                    <Field label="Reference">
                        <input className="e-in nto-mono" value={p.reference} placeholder="NTO-0000" onChange={(e) => edit((q) => ((q.reference = e.target.value), q))} />
                    </Field>
                    <Field label="Date">
                        <input className="e-in nto-mono" type="date" value={p.date} onChange={(e) => edit((q) => ((q.date = e.target.value), q))} />
                    </Field>
                    <Field label="Address" hint={`/${client.slug}/${id || "…"}`}>
                        <input
                            className="e-in nto-mono"
                            value={id}
                            placeholder="project-name"
                            onChange={(e) => {
                                setIdTouched(true)
                                edit((q) => ((q.id = slugify(e.target.value)), q))
                            }}
                        />
                    </Field>
                    <Field label="Intro" wide>
                        <textarea className="e-in e-area" rows={3} value={p.intro || ""} onChange={(e) => edit((q) => ((q.intro = e.target.value), q))} />
                    </Field>
                </div>
            </section>

            <div className="t-tabs-wrap" data-r>
                <nav className="t-tabs nto-label e-tabs" data-tab={tab}>
                    <TabInk tab={tab} />
                    {(["planning", "estimate"] as Tab[]).map((k) => (
                        <a
                            key={k}
                            href="#"
                            aria-current={tab === k ? "page" : undefined}
                            onClick={(e) => {
                                e.preventDefault()
                                setTab(k)
                            }}
                        >
                            {k === "planning" ? "Planning" : "Estimate"}
                        </a>
                    ))}
                </nav>
                <SaveImageButton project={{ ...p, id }} kind={tab} clientName={client.name} logo={client.logo} lang={client.lang} approval={approvalAtRisk ? null : approval} />
            </div>

            {tab === "planning" ? (
                <div className="t-panel">
                    {previewable ? (
                        <Planning entry={{ project: p, totals: t, version: "", approval: null }} dir={null} />
                    ) : (
                        <p className="e-empty nto-mono">The timeline shows once every phase has a start and an end.</p>
                    )}
                    <section className="p-section e-edit-section">
                        <h2 className="p-section-label nto-label">Phases</h2>
                        <div className="e-body">
                            {p.planning.phases.map((ph, i) => (
                                <div className="e-row e-phase" key={i}>
                                    <input className="e-in" placeholder="Phase" value={ph.name} onChange={(e) => edit((q) => ((q.planning.phases[i].name = e.target.value), q))} />
                                    <input className="e-in nto-mono" type="date" value={ph.start} onChange={(e) => edit((q) => ((q.planning.phases[i].start = e.target.value), q))} aria-label="Start" />
                                    <input className="e-in nto-mono" type="date" value={ph.end} onChange={(e) => edit((q) => ((q.planning.phases[i].end = e.target.value), q))} aria-label="End" />
                                    <input className="e-in e-note" placeholder="Note" value={ph.note || ""} onChange={(e) => edit((q) => ((q.planning.phases[i].note = e.target.value), q))} />
                                    <Order i={i} n={p.planning.phases.length} move={(to) => edit((q) => (move(q.planning.phases, i, to), q))} />
                                    <RemoveButton label="Remove phase" onClick={() => edit((q) => (q.planning.phases.splice(i, 1), q))} />
                                </div>
                            ))}
                            <button
                                type="button"
                                className="e-add nto-mono"
                                onClick={() =>
                                    edit((q) => {
                                        const last = q.planning.phases[q.planning.phases.length - 1]
                                        const start = last?.end ? addDays(last.end, 3) : addDays(iso(new Date()), 7)
                                        q.planning.phases.push({ name: "", start, end: addDays(start, 4) })
                                        return q
                                    })
                                }
                            >
                                + Phase
                            </button>
                        </div>
                    </section>
                    <section className="p-section e-edit-section">
                        <h2 className="p-section-label nto-label">Milestones</h2>
                        <div className="e-body">
                            {(p.planning.milestones || []).map((m, i) => (
                                <div className="e-row e-milestone" key={i}>
                                    <input className="e-in" placeholder="Milestone" value={m.name} onChange={(e) => edit((q) => ((q.planning.milestones![i].name = e.target.value), q))} />
                                    <input className="e-in nto-mono" type="date" value={m.date} onChange={(e) => edit((q) => ((q.planning.milestones![i].date = e.target.value), q))} />
                                    <RemoveButton label="Remove milestone" onClick={() => edit((q) => (q.planning.milestones!.splice(i, 1), q))} />
                                </div>
                            ))}
                            <button
                                type="button"
                                className="e-add nto-mono"
                                onClick={() => edit((q) => ((q.planning.milestones ||= []).push({ name: "", date: q.planning.phases.at(-1)?.end || iso(new Date()) }), q))}
                            >
                                + Milestone
                            </button>
                        </div>
                    </section>
                </div>
            ) : (
                <div className="t-panel">
                    <section className="p-section e-edit-section">
                        <h2 className="p-section-label nto-label">Estimate</h2>
                        <div className="e-body">
                            {approval && (
                                <p className="e-notice nto-mono">
                                    Approved on {fmtDate(approval.at.slice(0, 10), true)}.{" "}
                                    {approvalAtRisk ? "Saving these changes asks the client to approve again." : "Changing it asks the client to approve again."}
                                </p>
                            )}
                            <div className="e-grid e-settings">
                                <Field label="Currency">
                                    <select className="e-in nto-mono" value={p.estimate.currency} onChange={(e) => edit((q) => ((q.estimate.currency = e.target.value), q))}>
                                        {["EUR", "USD", "GBP", "CHF"].map((c) => (
                                            <option key={c}>{c}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="VAT %">
                                    <input className="e-in nto-mono" type="number" min={0} max={100} value={p.estimate.vat} onChange={(e) => edit((q) => ((q.estimate.vat = Number(e.target.value)), q))} />
                                </Field>
                                <Field label="Valid until">
                                    <input className="e-in nto-mono" type="date" value={p.estimate.validUntil} onChange={(e) => edit((q) => ((q.estimate.validUntil = e.target.value), q))} />
                                </Field>
                            </div>
                            {p.estimate.groups.map((g, gi) => (
                                <div className="e-group" key={gi}>
                                    <div className="e-row e-group-head">
                                        <input className="e-in nto-label" placeholder="GROUP" value={g.name} onChange={(e) => edit((q) => ((q.estimate.groups[gi].name = e.target.value), q))} />
                                        <Order i={gi} n={p.estimate.groups.length} move={(to) => edit((q) => (move(q.estimate.groups, gi, to), q))} />
                                        <RemoveButton label="Remove group" onClick={() => edit((q) => (q.estimate.groups.splice(gi, 1), q))} />
                                    </div>
                                    {g.lines.map((l, li) => (
                                        <LineRow
                                            key={li}
                                            line={l}
                                            currency={p.estimate.currency}
                                            change={(f) => edit((q) => (f(q.estimate.groups[gi].lines[li]), q))}
                                            order={<Order i={li} n={g.lines.length} move={(to) => edit((q) => (move(q.estimate.groups[gi].lines, li, to), q))} />}
                                            remove={() => edit((q) => (q.estimate.groups[gi].lines.splice(li, 1), q))}
                                        />
                                    ))}
                                    <button
                                        type="button"
                                        className="e-add nto-mono"
                                        onClick={() => edit((q) => (q.estimate.groups[gi].lines.push({ item: "", qty: 1, unit: g.lines.at(-1)?.unit || "days", rate: g.lines.at(-1)?.rate || 0 }), q))}
                                    >
                                        + Line
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="e-add nto-mono" onClick={() => edit((q) => (q.estimate.groups.push({ name: "", lines: [{ item: "", qty: 1, unit: "days", rate: 0 }] }), q))}>
                                + Group
                            </button>
                            <div className="p-sums">
                                <div className="p-sum nto-mono">
                                    <span>Subtotal</span>
                                    <span>{fmtMoney(t.net, p.estimate.currency)}</span>
                                </div>
                                <div className="p-sum nto-mono">
                                    <span>VAT {p.estimate.vat}%</span>
                                    <span>{fmtMoney(t.vat, p.estimate.currency)}</span>
                                </div>
                                <div className="p-total">
                                    <span className="nto-label">Total</span>
                                    <span className="p-total-amount">{fmtMoney(t.total, p.estimate.currency)}</span>
                                </div>
                            </div>
                            <Field label="Notes, one per line" wide>
                                <textarea
                                    className="e-in e-area nto-mono"
                                    rows={4}
                                    value={(p.estimate.notes || []).join("\n")}
                                    onChange={(e) => edit((q) => ((q.estimate.notes = e.target.value.split("\n")), q))}
                                />
                            </Field>
                        </div>
                    </section>
                </div>
            )}

            {!isNew && (
                <section className="p-section e-quiet-section">
                    <h2 className="p-section-label nto-label">Delete</h2>
                    <div className="e-body">
                        <Confirm label="Delete this project" sure="It's gone for good. Click again to delete" danger onYes={remove} />
                    </div>
                </section>
            )}

            <SaveBar
                dirty={dirty}
                state={state}
                note={approvalAtRisk ? "Unsaved: the client will approve again" : undefined}
                onSave={save}
                onDiscard={() => {
                    if (isNew) return go(studioPath(client.slug))
                    setP(base)
                    setState("idle")
                }}
            />
        </main>
    )
}

function LineRow(props: { line: Line; currency: string; change: (f: (l: Line) => void) => void; order: React.ReactNode; remove: () => void }) {
    const { line: l, currency, change } = props
    const fixed = l.unit === "fixed"
    return (
        <div className="e-row e-line">
            <div className="e-line-item">
                <input className="e-in" placeholder="Item" value={l.item} onChange={(e) => change((x) => (x.item = e.target.value))} />
                <input className="e-in e-note" placeholder="Note" value={l.note || ""} onChange={(e) => change((x) => (x.note = e.target.value))} />
            </div>
            <input
                className="e-in nto-mono e-num"
                type="number"
                min={0}
                step="0.5"
                value={l.qty}
                disabled={fixed}
                aria-label="Quantity"
                onChange={(e) => change((x) => (x.qty = Number(e.target.value)))}
            />
            <select
                className="e-in nto-mono"
                value={l.unit}
                aria-label="Unit"
                onChange={(e) =>
                    change((x) => {
                        x.unit = e.target.value
                        if (x.unit === "fixed") x.qty = 1
                    })
                }
            >
                {["days", "hours", "pieces", "fixed"].map((u) => (
                    <option key={u}>{u}</option>
                ))}
            </select>
            <input className="e-in nto-mono e-num" type="number" min={0} step="10" value={l.rate} aria-label={fixed ? "Price" : "Rate"} onChange={(e) => change((x) => (x.rate = Number(e.target.value)))} />
            <span className="nto-mono e-amount">{fmtMoney(l.qty * l.rate, currency)}</span>
            {props.order}
            <RemoveButton label="Remove line" onClick={props.remove} />
        </div>
    )
}

/** move up / down */
function Order({ i, n, move: to }: { i: number; n: number; move: (to: number) => void }) {
    return (
        <span className="e-order">
            <button type="button" disabled={i === 0} onClick={() => to(i - 1)} aria-label="Move up" title="Move up">
                ↑
            </button>
            <button type="button" disabled={i === n - 1} onClick={() => to(i + 1)} aria-label="Move down" title="Move down">
                ↓
            </button>
        </span>
    )
}

function move<T>(list: T[], from: number, to: number) {
    const [x] = list.splice(from, 1)
    list.splice(to, 0, x)
}

/** the white pill under the current tab (as on the client's page) */
function TabInk({ tab }: { tab: Tab }) {
    const ink = React.useRef<HTMLSpanElement>(null)
    const first = React.useRef(true)
    React.useLayoutEffect(() => {
        const el = ink.current
        const a = el?.parentElement?.querySelector<HTMLElement>("[aria-current]")
        if (!el || !a) return
        const place = () => {
            el.style.width = a.offsetWidth + "px"
            el.style.transform = `translateX(${a.offsetLeft}px)`
        }
        if (first.current) {
            el.style.transition = "none"
            place()
            void el.offsetWidth
            el.style.transition = ""
            first.current = false
            document.fonts?.ready.then(place)
        } else place()
    }, [tab])
    return <span ref={ink} className="t-ink" aria-hidden="true" />
}
